import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Phone, Mail, MessageSquare, Send, PhoneCall, PhoneOff,
  Delete, Loader2, BookUser, User, Clock,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Skeleton } from "../components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import api, { apiError } from "../lib/api";
import { formatDateTime, formatPhone } from "../lib/utils-format";

const RESOURCE_LABEL = {
  property_search: "Property Screening", leads: "Property Analysis", projects: "Project",
  acquisition: "Acquisition", planning: "Planning", opportunities: "Design",
  preconstruction: "Preconstruction", construction: "Construction", closeout: "Closeout",
};

const CH_ICON = { Phone: PhoneCall, SMS: MessageSquare, Email: Mail };
const STATUS_STYLE = {
  Delivered: "bg-emerald-500/15 text-emerald-400",
  Completed: "bg-emerald-500/15 text-emerald-400",
  "In Progress": "bg-amber-500/15 text-amber-400",
  Sent: "bg-amber-500/15 text-amber-400",
  Failed: "bg-red-500/15 text-red-400",
};
const CALL_OUTCOMES = ["Connected", "Left Voicemail", "No Answer", "Busy", "Wrong Number"];
const KEYPAD = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"];

function recordName(r) {
  if (!r) return "Record";
  return r.project_name || `${r.first_name || ""} ${r.last_name || ""}`.trim() ||
    r.company_name || r.property_name || r.name || "Record";
}

export default function CommunicationPage() {
  const { resource, id } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialMode = params.get("mode");
  const [tab, setTab] = useState(["call", "sms", "email"].includes(initialMode) ? initialMode : "call");

  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [counts, setCounts] = useState({ all: 0, Phone: 0, SMS: 0, Email: 0 });
  const [filter, setFilter] = useState("all");

  const [contacts, setContacts] = useState([]);
  const [contactName, setContactName] = useState("");

  // channel fields
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [smsBody, setSmsBody] = useState("");
  const [subject, setSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [sending, setSending] = useState(false);

  // active call state
  const [activeCall, setActiveCall] = useState(null);
  const [seconds, setSeconds] = useState(0);
  const timerRef = useRef(null);

  const name = recordName(record);

  const loadHistory = useCallback(() => {
    return api.get(`/comm/history/${resource}/${id}`).then((r) => {
      setHistory(r.data.items || []);
      setCounts(r.data.counts || { all: 0 });
    }).catch(() => {});
  }, [resource, id]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/${resource}/${id}`).then((r) => r.data).catch(() => null),
      loadHistory(),
      api.get(`/directory?limit=500`).then((r) => r.data.items || []).catch(() => []),
    ]).then(([rec, _h, dir]) => {
      setRecord(rec);
      if (rec) {
        const recPhone = rec.owner_phone || rec.mobile_phone || rec.phone || rec.business_phone || "";
        const recEmail = rec.owner_email || rec.email || "";
        if (recPhone) setPhone((p) => p || recPhone);
        if (recEmail) setEmail((e) => e || recEmail);
      }
      setContacts(dir);
    }).finally(() => setLoading(false));
  }, [resource, id, loadHistory]);

  useEffect(() => () => clearInterval(timerRef.current), []);

  const pickContact = (cid) => {
    const c = contacts.find((x) => x.id === cid);
    if (!c) return;
    const nm = `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.company_name || "Contact";
    setContactName(nm);
    setPhone(c.mobile_phone || c.business_phone || "");
    setEmail(c.email || "");
    toast.success(`Loaded ${nm} from Directory`);
  };

  const fmtDur = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  const startCall = async () => {
    if (!phone.trim()) { toast.error("Enter a phone number to call"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/comm/call", {
        resource, record_id: id, record_name: name, to: phone, contact_name: contactName || null,
      });
      setActiveCall(data);
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
      if (data.mock) toast.info("Call placed in demo mode (no Twilio keys configured yet)");
      else toast.success("Call placed");
    } catch (e) { toast.error(apiError(e)); } finally { setSending(false); }
  };

  const endCall = async (outcome) => {
    clearInterval(timerRef.current);
    const dur = fmtDur(seconds);
    try {
      await api.post("/comm/call/hangup", { call_id: activeCall.id, outcome, duration: dur });
      toast.success(`Call ended — ${outcome}`);
    } catch (e) { toast.error(apiError(e)); }
    setActiveCall(null);
    setSeconds(0);
    await loadHistory();
  };

  const sendSms = async () => {
    if (!phone.trim()) { toast.error("Enter a phone number"); return; }
    if (!smsBody.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post("/comm/sms", { resource, record_id: id, record_name: name, to: phone, body: smsBody });
      setSmsBody("");
      await loadHistory();
      if (data.mock) toast.info("SMS logged in demo mode (no Twilio keys configured yet)");
      else toast.success(`SMS sent to ${phone}`);
    } catch (e) { toast.error(apiError(e)); } finally { setSending(false); }
  };

  const sendEmail = async () => {
    if (!email.trim()) { toast.error("Enter an email address"); return; }
    if (!emailBody.trim() || !subject.trim()) { toast.error("Subject and message are required"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/comm/email", { resource, record_id: id, record_name: name, to: email, subject, body: emailBody });
      setEmailBody(""); setSubject("");
      await loadHistory();
      if (data.mock) toast.info("Email logged in demo mode (no email keys configured yet)");
      else toast.success(`Email sent to ${email}`);
    } catch (e) { toast.error(apiError(e)); } finally { setSending(false); }
  };

  const visible = filter === "all" ? history : history.filter((h) => h.channel === filter);

  const TABS = [
    { key: "call", label: "Call", Icon: Phone },
    { key: "sms", label: "SMS", Icon: MessageSquare },
    { key: "email", label: "Email", Icon: Mail },
  ];

  return (
    <div className="p-6 lg:p-8 max-w-[900px] mx-auto">
      <button onClick={() => navigate(`/record/${resource}/${id}`)} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-5" data-testid="comm-back-btn">
        <ArrowLeft className="h-4 w-4" />Back to record
      </button>

      <div className="mb-6 pb-6 border-b border-border">
        <div className="text-[11px] uppercase tracking-[0.18em] text-primary mb-1.5">{RESOURCE_LABEL[resource] || "Record"} · Communication</div>
        <h1 className="font-display text-3xl font-bold tracking-tight flex items-center gap-3" data-testid="comm-title">
          <PhoneCall className="h-6 w-6 text-primary" />{loading ? "…" : name}
        </h1>
        <p className="text-sm text-muted-foreground mt-1.5">Call, text, and email — every interaction is logged below.</p>
      </div>

      {loading ? (
        <div className="space-y-3"><Skeleton className="h-40" /><Skeleton className="h-24" /></div>
      ) : (
        <>
          {/* Contact selector */}
          <div className="rounded-lg border border-border bg-card p-4 mb-5" data-testid="comm-contact-block">
            <div className="flex items-center gap-2 mb-3 text-sm font-semibold"><BookUser className="h-4 w-4 text-primary" />Contact</div>
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wide text-muted-foreground">From Directory</label>
                <Select onValueChange={pickContact}>
                  <SelectTrigger data-testid="comm-directory-select" className="mt-1"><SelectValue placeholder="Pick a contact…" /></SelectTrigger>
                  <SelectContent>
                    {contacts.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">No directory entries</div>}
                    {contacts.map((c) => {
                      const nm = `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.company_name || "Contact";
                      return <SelectItem key={c.id} value={c.id}>{nm}{c.company_name && nm !== c.company_name ? ` · ${c.company_name}` : ""}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wide text-muted-foreground">Phone</label>
                <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 555 000 0000" className="mt-1" data-testid="comm-phone-input" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wide text-muted-foreground">Email</label>
                <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@email.com" className="mt-1" data-testid="comm-email-input" />
              </div>
            </div>
            {contactName && <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-muted-foreground"><User className="h-3 w-3" />{contactName}</div>}
          </div>

          {/* Channel tabs */}
          <div className="rounded-lg border border-border bg-card mb-6" data-testid="comm-composer">
            <div className="flex items-center gap-1 px-4 pt-3 border-b border-border">
              {TABS.map(({ key, label, Icon }) => (
                <button key={key} onClick={() => setTab(key)} data-testid={`comm-tab-${key}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${tab === key ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                  <Icon className="h-3.5 w-3.5" />{label}
                </button>
              ))}
            </div>

            <div className="p-5">
              {/* CALL */}
              {tab === "call" && (
                <div data-testid="comm-call-panel">
                  {!activeCall ? (
                    <>
                      <div className="max-w-[280px] mx-auto">
                        <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Enter number" className="text-center text-lg tracking-wider mb-3" data-testid="dialer-number" />
                        <div className="grid grid-cols-3 gap-2">
                          {KEYPAD.map((k) => (
                            <button key={k} onClick={() => setPhone((p) => p + k)} data-testid={`dialer-key-${k}`}
                              className="h-12 rounded-md border border-border bg-secondary/40 text-lg font-semibold hover:bg-accent transition-colors">{k}</button>
                          ))}
                        </div>
                        <div className="flex items-center gap-2 mt-3">
                          <Button variant="outline" size="icon" onClick={() => setPhone((p) => p.slice(0, -1))} data-testid="dialer-backspace"><Delete className="h-4 w-4" /></Button>
                          <Button className="flex-1 bg-emerald-600 hover:bg-emerald-500" onClick={startCall} disabled={sending} data-testid="dialer-call-btn">
                            {sending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <PhoneCall className="h-4 w-4 mr-1.5" />}Call
                          </Button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-4" data-testid="active-call-panel">
                      <div className="inline-flex items-center gap-2 text-emerald-400 font-semibold"><span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />On call</div>
                      <div className="text-3xl font-display font-bold tracking-tight mt-2" data-testid="call-timer"><Clock className="h-5 w-5 inline mr-2 text-muted-foreground" />{fmtDur(seconds)}</div>
                      <div className="text-sm text-muted-foreground mt-1">{contactName || formatPhone(phone) || phone}</div>
                      <div className="text-[11px] uppercase tracking-wide text-muted-foreground mt-4 mb-2">End call & log outcome</div>
                      <div className="flex flex-wrap gap-2 justify-center">
                        {CALL_OUTCOMES.map((o) => (
                          <Button key={o} variant="outline" size="sm" onClick={() => endCall(o)} data-testid={`call-outcome-${o.replace(/\s+/g, "-").toLowerCase()}`}>{o}</Button>
                        ))}
                        <Button variant="destructive" size="sm" onClick={() => endCall("Ended")} data-testid="call-hangup-btn"><PhoneOff className="h-4 w-4 mr-1.5" />Hang up</Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* SMS */}
              {tab === "sms" && (
                <div data-testid="comm-sms-panel">
                  <Textarea value={smsBody} onChange={(e) => setSmsBody(e.target.value)} placeholder={`Text ${contactName || "the contact"}…`} rows={4} data-testid="sms-input" />
                  <div className="flex justify-end mt-2">
                    <Button size="sm" onClick={sendSms} disabled={sending || !smsBody.trim()} data-testid="sms-send-btn">
                      {sending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />}Send SMS
                    </Button>
                  </div>
                </div>
              )}

              {/* EMAIL */}
              {tab === "email" && (
                <div data-testid="comm-email-panel">
                  <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" className="mb-2" data-testid="email-subject-input" />
                  <Textarea value={emailBody} onChange={(e) => setEmailBody(e.target.value)} placeholder="Write your email…" rows={6} data-testid="email-body-input" />
                  <div className="flex justify-end mt-2">
                    <Button size="sm" onClick={sendEmail} disabled={sending || !emailBody.trim() || !subject.trim()} data-testid="email-send-btn">
                      {sending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />}Send Email
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* HISTORY */}
          <div data-testid="comm-history">
            <div className="flex items-center gap-1 mb-3 border-b border-border">
              {[["all", "All"], ["Phone", "Calls"], ["SMS", "SMS"], ["Email", "Email"]].map(([val, lbl]) => (
                <button key={val} onClick={() => setFilter(val)} data-testid={`comm-hist-filter-${val.toLowerCase()}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${filter === val ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                  {lbl}<span className="text-[10px] px-1.5 py-0.5 rounded-full bg-secondary">{counts[val] ?? 0}</span>
                </button>
              ))}
            </div>

            {visible.length === 0 ? (
              <div className="rounded-lg border border-border bg-card p-10 text-center text-muted-foreground text-sm" data-testid="comm-history-empty">
                No communications logged yet.
              </div>
            ) : (
              <div className="space-y-2.5">
                {visible.map((h) => {
                  const Icon = CH_ICON[h.channel] || Mail;
                  return (
                    <div key={h.id} className="rounded-lg border border-border bg-card p-4" data-testid={`comm-hist-item-${h.id}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2 text-sm font-medium">
                          <Icon className="h-4 w-4 text-primary" />
                          {h.channel === "Phone" ? "Call" : h.channel}
                          <span className="text-xs text-muted-foreground">· {h.sender || "You"}</span>
                          {h.mock && <span className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-secondary text-muted-foreground">demo</span>}
                        </div>
                        <div className="flex items-center gap-2">
                          {h.channel === "Phone" && h.duration && h.duration !== "0:00" && <span className="text-[10px] text-muted-foreground">{h.duration}</span>}
                          {(h.status || h.outcome) && (
                            <span className={`text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded-full font-medium ${STATUS_STYLE[h.status] || "bg-secondary text-muted-foreground"}`}>
                              {h.outcome || h.status}
                            </span>
                          )}
                        </div>
                      </div>
                      {h.subject && <div className="text-sm font-medium">{h.subject}</div>}
                      {h.body && <div className="text-sm text-muted-foreground whitespace-pre-wrap">{h.body}</div>}
                      {h.notes && <div className="text-sm text-muted-foreground">{h.notes}</div>}
                      <div className="text-[10px] text-muted-foreground mt-1.5">
                        {(h.to || h.to_number) ? `${h.to || h.to_number} · ` : ""}{formatDateTime(h.created_at)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
